import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { subscribePaginatedAggregation } from '@adornis/baseql/server/resolver/mongo.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { EntityPatch } from '@adornis/versioned-entity/db/versioned-mongo-entity.js';
import { projectConfig } from '@chancenwerk/config.js';
import { KpfFilter } from '@chancenwerk/db/filter/KpfFilter.js';
import { KPFAPI } from '@chancenwerk/master-data-management/kpf/api/KPFAPI.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { chancenWerkMailer } from '@chancenwerk/server/mailer.js';
import { simpleMail } from '@chancenwerk/server/mailTemplates.js';
import { translationData } from '@chancenwerk/translation-data.js';
import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DateTime } from 'luxon';
import { firstValueFrom, switchMap } from 'rxjs';
import { CapacityGuestDay } from '../entities/CapacityGuestDay.js';
import { CapacityRequest, CapacityRequestStatus, CapacityRequestType } from '../entities/CapacityRequest.js';
import { CapacityRequestOverview } from '../entities/CapacityRequestOverview.js';

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '(leer)';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat('dd.MM.yyyy HH:mm');
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return DateTime.fromISO(value).toFormat('dd.MM.yyyy HH:mm');
  }
  return String(value);
};

export namespace CapacityAPI {
  /**
   * Subscribe all capacity requests
   */
  export const subscribeOverview = registerSubscription({
    operationName: 'CapacityAPI_subscribeOverview',
    type: () => [CapacityRequestOverview],
    resolve: (filter: KpfFilter) => {
      return (gqlFields: BaseQLSelectionSet<CapacityRequestOverview>) => {
        return watchQuery(CapacityRequest._collectionName, {}).pipe(
          switchMap(async () => {
            const validKPFs = await firstValueFrom(KPFAPI.subscribeByFilter(0, 1000, filter)({ _id: 1 }));
            const col = await getRawCollection<CapacityRequest>(CapacityRequest._collectionName);
            const entries = await col
              .aggregate([
                {
                  $match: {
                    KPFID: { $in: validKPFs.map(k => k._id) },
                    status: { $ne: CapacityRequestStatus.HIRED },
                  },
                },

                {
                  $group: {
                    _id: '$KPFID',
                    leko: { $sum: { $cond: [{ $eq: ['$type', CapacityRequestType.LEKO] }, 1, 0] } },
                    schuko: { $sum: { $cond: [{ $eq: ['$type', CapacityRequestType.SCHUKO] }, 1, 0] } },
                    highPriority: { $max: { $cond: [{ $eq: ['$highPriority', true] }, true, false] } },
                  },
                },

                {
                  $project: {
                    _class: 'CapacityRequestOverview',
                    KPFID: '$_id',
                    schuko: 1,
                    leko: 1,
                    highPriority: 1,
                  },
                },
              ])
              .toArray();

            // * create missing entries with 0 capacity
            const existing = entries.map(entry => entry.KPFID);
            validKPFs.forEach(kpf => {
              if (!existing.includes(kpf._id)) {
                entries.push(
                  new CapacityRequestOverview({ KPFID: kpf._id, leko: 0, schuko: 0, highPriority: false }).toJSON(),
                );
              }
            });

            return (entries as CapacityRequestOverview[]).sort((a, b) => {
              const ASum = (a.leko ?? 0) + (a.schuko ?? 0);
              const BSum = (b.leko ?? 0) + (b.schuko ?? 0);
              // First sort by sum, then by _id for stability when sums are equal
              return BSum !== ASum ? BSum - ASum : (a.KPFID ?? '').localeCompare(b.KPFID ?? '');
            });
          }),
        );
      };
    },
    params: [{ name: 'filter', type: () => KpfFilter }],
  });

  /**
   * Subscribe all capacity requests for a specific KPF
   */
  export const subscribeByKPF = registerSubscription({
    operationName: 'CapacityAPI_subscribeByKPF',
    type: () => [CapacityRequest],
    resolve: (kpfID: string, skip: number, limit: number, filter: AdornisFilter) => {
      return () => {
        return subscribePaginatedAggregation(
          CapacityRequest,
          skip,
          limit,
          {
            KPFID: kpfID,
            ...filter.getMongoFilter(),
          },
          [{ $sort: filter.getMongoOptions().sort }],
        );
      };
    },
    params: [
      { name: 'kpfID', type: () => String },
      { name: 'skip', type: () => Int },
      { name: 'limit', type: () => Int },
      { name: 'filter', type: () => AdornisFilter },
    ],
  });

  /**
   * Get all reference IDs for a specific KPF
   */
  export const getReferenceIDsForKPF = registerQuery({
    operationName: 'CapacityAPI_getReferenceIDsForKPF',
    type: () => [String],
    resolve: (kpfID: string) => {
      return async () => {
        return (await loadMongoData(CapacityRequest, { KPFID: kpfID })).map(r => r._id);
      };
    },
    params: [{ name: 'kpfID', type: () => String }],
  });

  /**
   * Notify a new capacity request
   */
  export const notifyNew = registerMutation({
    operationName: 'CapacityAPI_notifyNew',
    type: () => Boolean,
    resolve: (rid: String) => {
      return async () => {
        const req = await getByID(
          CapacityRequest,
          rid,
        )({
          type: 1,
          fromWhen: 1,
          weekdays: 1,
          specialSkills: 1,
          highPriority: 1,
          KPF: {
            _id: 1,
            kuerzel: 1,
            produkt: {
              _class: 1,
              kuerzel: 1,
            },
            school: {
              name: 1,
              kuerzel: 1,
            },
            personsOperativ: {
              email: 1,
            },
          },
        });

        await setNotify(req?.KPF?._id ?? '', true)();

        const email = req?.KPF?.personsOperativ?.[0]?.email;
        if (!email || !req?.KPF?._id) {
          return;
        }

        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: `CHANCENkräfte neuer Bedarf (${req?.type} / ${req?.KPF?.kuerzel})`,
            html: simpleMail(
              `CHANCENkräfte neuer Bedarf (${req?.type} / ${req?.KPF?.kuerzel})`,
              html`
                Ein neuer Bedarf in den CHANCENkräften wurde angelegt!
                <br />
                <br />
                <b>KPF:</b> ${req?.KPF?.kuerzel} <br />
                <b>Art:</b> ${req?.type} <br />
                <b>Ab wann:</b> ${req?.fromWhen?.toFormat('dd.MM.yyyy')} <br />
                <b>Wochentage:</b> ${req?.weekdays?.join(', ')} <br />
                <b>besondere Fähigkeiten:</b> ${req?.specialSkills} <br />
                <b>hohe Priorität:</b> ${req?.highPriority}
                <br />
                <br />
                <a href="${projectConfig.get('KRAEFTE_URL')}capacity/${req?.KPF?._id}">zum Detailbild</a>
              `,
            ),
          }),
          new Recipient(email),
        );
      };
    },
    params: [{ name: 'rid', type: () => String }],
  });

  /**
   * Notify a capacity request change
   */
  export const notifyChange = registerMutation({
    operationName: 'CapacityAPI_notifyChange',
    type: () => Boolean,
    resolve: (rid: String) => {
      return async () => {
        const req = await getByID(
          CapacityRequest,
          rid,
        )({
          _id: 1,
          type: 1,
          fromWhen: 1,
          weekdays: 1,
          specialSkills: 1,
          highPriority: 1,
          KPF: {
            _id: 1,
            kuerzel: 1,
            produkt: {
              _class: 1,
              kuerzel: 1,
            },
            school: {
              name: 1,
              kuerzel: 1,
            },
            personsOperativ: {
              email: 1,
            },
          },
        });

        await setNotify(req?.KPF?._id ?? '', true)();

        const email = req?.KPF?.personsOperativ?.[0]?.email;
        if (!email || !req?.KPF?._id) {
          return;
        }

        const versionCollection = await getRawCollection<EntityPatch>('entityversions');
        const latest = await versionCollection.findOne(
          {
            referenceID: req._id,
          },
          {
            sort: {
              timestamp: -1,
            },
          },
        );

        const translate = (path: string[]) => {
          return path
            .map(p => translationData.field[p]?.[0])
            .filter(Boolean)
            .join(', ');
        };

        let weekdaysPrinted = false;
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: `CHANCENkräfte Bedarf bearbeitet (${req?.type} / ${req?.KPF?.kuerzel})`,
            html: simpleMail(
              `CHANCENkräfte Bedarf bearbeitet (${req?.type} / ${req?.KPF?.kuerzel})`,
              html`
                Ein Bedarf wurde bearbeitet.
                <br />
                <br />
                <hr />
                <br />
                ${latest?.patches?.map(patch => {
                  if (patch.op !== 'replace' && patch.op !== 'remove' && patch.op !== 'add') return;

                  const path = patch.path?.split('/') ?? [];
                  const field = translate(path);
                  const before = latest?.patches?.find(p => p.op === 'test' && p.path === patch.path);

                  if (['add', 'remove'].includes(patch.op)) {
                    if (weekdaysPrinted) {
                      return '';
                    }
                    weekdaysPrinted = true;
                    return html`<b>${field}</b>: <br />
                      ${req.weekdays?.join(', ')}<br /><br /> `;
                  }

                  if (
                    !field ||
                    ((before?.value === null || before?.value === undefined || before?.value === '') &&
                      (patch?.value === null || patch?.value === undefined || patch?.value === ''))
                  ) {
                    return;
                  }

                  return html`<b>${field}</b>: <br />
                    ${formatValue(before?.value)} → ${formatValue(patch?.value)} <br /><br /> `;
                })}
                <hr />
                <br />
                <a href="${projectConfig.get('KRAEFTE_URL')}capacity/${req?.KPF?._id}">zum Detailbild</a>
              `,
            ),
          }),
          new Recipient(email),
        );
      };
    },
    params: [{ name: 'rid', type: () => String }],
  });

  /**
   * Notify a capacity request comment
   */
  export const notifyComment = registerMutation({
    operationName: 'CapacityAPI_notifyComment',
    type: () => Boolean,
    resolve: (kpfID: string, comment: string) => {
      return async () => {
        const kpf = await getByID(
          KPF<any>,
          kpfID,
        )({
          _id: 1,
          kuerzel: 1,
          produkt: {
            _class: 1,
            kuerzel: 1,
          },
          school: {
            name: 1,
            kuerzel: 1,
          },
          personsOperativ: {
            email: 1,
          },
        });

        await setNotify(kpf?._id ?? '', true)();

        const email = kpf?.personsOperativ?.[0]?.email;
        if (!email || !kpf?._id) {
          return;
        }

        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: `CHANCENkräfte Kommentar (${kpf?.kuerzel})`,
            html: simpleMail(
              `CHANCENkräfte Kommentar (${kpf?.kuerzel})`,
              html`
                Ein Kommentar wurde erstellt.
                <br />
                <br />
                <hr />
                <br />
                <strong>Kommentar:</strong><br />
                ${comment}
                <br />
                <hr />
                <br />
                <a href="${projectConfig.get('KRAEFTE_URL')}capacity/${kpf?._id}">zum Detailbild</a>
              `,
            ),
          }),
          new Recipient(email),
        );
      };
    },
    params: [
      { name: 'kpfID', type: () => String },
      { name: 'comment', type: () => String },
    ],
  });

  /**
   * Notify about a deleted capacity request
   */
  export const notifyDelete = registerMutation({
    operationName: 'CapacityAPI_notifyDelete',
    type: () => Boolean,
    resolve: (kpfID: string, type: string) => {
      return async () => {
        const kpf = await getByID(
          KPF<any>,
          kpfID,
        )({
          _id: 1,
          kuerzel: 1,
          produkt: {
            _class: 1,
            kuerzel: 1,
          },
          school: {
            name: 1,
            kuerzel: 1,
          },
          personsOperativ: {
            email: 1,
          },
        });

        await setNotify(kpf?._id ?? '', true)();

        const email = kpf?.personsOperativ?.[0]?.email;
        if (!email || !kpf?._id) {
          return;
        }

        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: `CHANCENkräfte Bedarf gelöscht (${type} / ${kpf.kuerzel})`,
            html: simpleMail(
              `CHANCENkräfte Bedarf gelöscht (${type} / ${kpf.kuerzel})`,
              html`
                Ein Bedarf wurde gelöscht.
                <br />
                <br />
                <hr />
                <br />
                <b>KPF:</b> ${kpf.kuerzel} <br />
                <b>Art:</b> ${type} <br />
                <br />
                <hr />
                <br />
                <a href="${projectConfig.get('KRAEFTE_URL')}capacity/${kpf._id}">zum Detailbild</a>
              `,
            ),
          }),
          new Recipient(email),
        );
      };
    },
    params: [
      { name: 'kpfID', type: () => String },
      { name: 'type', type: () => String },
    ],
  });

  /**
   * Set a capacity request as notified
   */
  export const setNotify = registerMutation({
    operationName: 'CapacityAPI_setNotify',
    type: () => Boolean,
    resolve: (kpfId: String, notify: boolean) => {
      return async () => {
        const col = await getRawCollection<{ notification: boolean }>('capacity-request-meta');
        await col.updateOne({ _id: kpfId }, { $set: { notification: notify } }, { upsert: true });
        return true;
      };
    },
    params: [
      { name: 'kpfId', type: () => String },
      { name: 'notify', type: () => Boolean },
    ],
  });

  export const notifyGuestDays = registerMutation({
    operationName: 'CapacityAPI_notifyGuestDays',
    type: () => Boolean,
    resolve: () => {
      return async () => {
        // Find all GuestDays that are today
        const days = (await loadMongoData(CapacityGuestDay, {
          day: {
            $gte: DateTime.now().startOf('day').toJSDate(),
            $lt: DateTime.now().endOf('day').toJSDate(),
          },
        })) as EntityData<CapacityGuestDay>[];

        // chunk by KPFID
        const byKPF: Record<string, EntityData<CapacityGuestDay>[]> = {};
        for (const day of days) {
          if (!day.KPFID) {
            continue;
          }
          if (!byKPF[day.KPFID]) {
            byKPF[day.KPFID] = [];
          }
          byKPF[day.KPFID]?.push(day);
        }

        // Notify each KPF
        for (const kpfId in byKPF) {
          const days = byKPF[kpfId];
          const daysResolved = await getByID(
            CapacityGuestDay,
            days?.map(d => d._id) ?? [],
          )({
            request: {
              type: 1,
            },
            day: 1,
            vorname: 1,
            nachname: 1,
            telefonBewerber: 1,
          });

          const kpf = await getByID(
            KPF<any>,
            kpfId,
          )({
            _id: 1,
            schuKos: {
              email: 1,
            },
            personsOperativ: {
              email: 1,
            },
            produkt: {
              kuerzel: 1,
              class: 1,
            },
            school: {
              kuerzel: 1,
            },
            kuerzel: 1,
          });

          if (!kpf) {
            continue;
          }

          const daysInfo =
            daysResolved
              ?.map(c => {
                return `<li>${c.request?.type ?? ''}, ${c.day?.toFormat('dd.MM.yyyy')}, ${c.vorname} ${c.nachname}, ${
                  c.telefonBewerber
                }</li>`;
              })
              .join('') ?? '';

          const emails = [
            ...(kpf.schuKos?.map(s => s.email) ?? []),
            ...(kpf.personsOperativ?.map(p => p.email) ?? []),
          ].filter(Boolean);
          if (emails.length === 0) {
            continue;
          }

          for (const email of emails) {
            try {
              await chancenWerkMailer.sendMail(
                await Message.compose({
                  subject: `CHANCENkräfte Schnuppertag Heute (${kpf.kuerzel})`,
                  html: simpleMail(
                    `CHANCENkräfte Schnuppertag Heute (${kpf.kuerzel})`,
                    html`
                      <b>Schnuppertag Heute</b> für ${kpf.kuerzel}
                      <br />
                      <br />
                      <hr />
                      <br />
                      <b>Für den heutigen Tag sind folgende Schnuppertage geplant:</b>
                      <br />
                      <br />
                      <ul>
                        ${unsafeHTML(daysInfo)}
                      </ul>
                      <br />
                    `,
                  ),
                }),
                new Recipient(email),
              );
            } catch (e) {
              logger.error(e, 'Error sending email');
            }
          }
        }

        return true;
      };
    },
    params: [],
  });
}
